import { useEffect, useState } from 'react';
import { useAppDispatch } from 'store/hooks/useApp';
import { aiChatsApi } from 'store/slices/aiChats/apis/aiChatsApi';

import { DATASOURCE_ROUTE_MAX_LIMIT } from '../constants/datasource';
import readDatasourceDroppedReason from '../utils/readDatasourceDroppedReason';

import type IAiChatDatasourceRows from '../interfaces/IAiChatDatasourceRows';

const EMPTY: IAiChatDatasourceRows = {
  columns: [], rows: [], executedAt: null, droppedReason: null, isLoading: true, isError: false,
};

/** Thrown for a page the route refused; carries the 422 reason when that is what it answered. */
class DatasourcePageError extends Error {
  constructor(readonly droppedReason: string | null) {
    super('A page of the datasource could not be read.');
  }
}

/**
 * Every row of a datasource, for a display that needs them all at once — a chart.
 *
 * The route pages, so the rows come in as many requests as `rowCount` needs at the route's widest
 * page, one after the other, and are handed over whole. Each page goes through the RTK cache, so
 * a chart drawn twice — a live turn and the same chat from history — asks the server once.
 *
 * A datasource whose rows were dropped is run again by the route on the first page. One whose
 * statement no longer runs answers 422: the read stops there, the reason is handed over as
 * `droppedReason`, and the route is not asked again.
 */
const useAiChatDatasourceRows = (datasourceId: number, rowCount: number): IAiChatDatasourceRows => {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<IAiChatDatasourceRows>(EMPTY);

  useEffect(() => {
    let isCurrent = true;
    setState(EMPTY);

    const readAll = async () => {
      const rows: unknown[][] = [];
      let columns: IAiChatDatasourceRows['columns'] = [];
      let executedAt: string | null = null;

      for (let offset = 0; offset === 0 || offset < rowCount; offset += DATASOURCE_ROUTE_MAX_LIMIT) {
        // Pages are read in order, each one after the last: the rows must be handed over in order.
        // eslint-disable-next-line no-await-in-loop
        const { data, error } = await dispatch(aiChatsApi.endpoints.getAiChatDatasourcePage.initiate({
          datasourceId, offset, limit: DATASOURCE_ROUTE_MAX_LIMIT,
        }));

        if (error || !data) {
          throw new DatasourcePageError(readDatasourceDroppedReason(error));
        }

        ({ columns, executedAt } = data);
        rows.push(...data.rows);
      }

      return { columns, rows, executedAt };
    };

    readAll()
      .then(({ columns, rows, executedAt }) => {
        if (isCurrent) {
          setState({
            ...EMPTY, columns, rows, executedAt, isLoading: false,
          });
        }
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          const droppedReason = error instanceof DatasourcePageError ? error.droppedReason : null;

          setState({
            ...EMPTY, droppedReason, isLoading: false, isError: droppedReason === null,
          });
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [datasourceId, rowCount, dispatch]);

  return state;
};

export default useAiChatDatasourceRows;
